import { OrganizationResource, PartnerResource, generateConsentLink } from 'api'
import { ConnectionStatus } from 'api/digitalMarketing/types'
import { TypeName } from 'api/models'
import { Section, Table, TableMobile } from 'components'
import ChilipiperSection from 'components/ChilipiperSection'
import ConnectionModal from 'components/ConnectionModal/ConnectionModal'
import SectionHeader from 'components/Section/SectionHeader'
import SectionTitle from 'components/Section/SectionTitle'
import { appUrl } from 'config'
import { useGetMarketingChannelByPartnerId, useIsMobile } from 'hooks'
import { isEmpty } from 'lodash'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  MarketingChannelObject,
  MarketingChannelTypes,
} from 'types/digitalMarketing/marketing-channel'
import { getMenuManagementGBPProfileSelectorURL } from 'utils'
import { v4 as uuidv4 } from 'uuid'

import { getConnectionColumns } from './columns'
import GoogleBusinessProfileDetailsDrawer from './GoogleBusinessProfileDrawer/GoogleBusinessProfileDrawer'

type Props = {
  location: PartnerResource
  organization?: OrganizationResource
  isLoading: boolean
  sectionTitle: string
  locationName: string
}

const DigitalMarketingConnections = ({
  location,
  organization,
  isLoading,
  sectionTitle,
  locationName,
}: Props) => {
  const { isMobile } = useIsMobile()
  const { marketingChannels } = useGetMarketingChannelByPartnerId(location.id)
  const [selectedGBPMarketingChannelId, setSelectedGBPMarketingChannelId] = useState('')
  const [searchParams] = useSearchParams()

  const marketingChannelIdParam = searchParams.get('marketingChannelId')

  const [connectionModalOpen, setConnectionModalOpen] = useState(Boolean(marketingChannelIdParam))

  const TableComponent = isMobile ? TableMobile : Table

  const isRCCPlatform = organization?.attributes?.chowly_platform

  const connectToGoogleBusinessProfile = useCallback(async () => {
    if (location.id) {
      const url = await generateConsentLink(
        `${appUrl}${getMenuManagementGBPProfileSelectorURL(location.id)}`,
        location.id,
      )
      const width = 500
      const height = 600
      const left = (window.innerWidth - width) / 2
      const top = (window.innerHeight - height) / 2
      if (url)
        window.open(
          url,
          'GoogleConsentPopup',
          `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`,
        )
    }
  }, [location.id])

  const openGoogleBusinessProfile = useCallback(async (marketingChannelId: string) => {
    setSelectedGBPMarketingChannelId(marketingChannelId)
  }, [])

  const connectionColumns = useMemo(() => {
    if (!location.id) {
      return []
    }
    let status: ConnectionStatus = 'NOT_INTEGRATED'
    const marketingChannel = marketingChannels?.find(
      (mc: MarketingChannelObject) => mc.type === MarketingChannelTypes.GoogleBusinessProfile,
    )
    if (
      !isEmpty(marketingChannel?.googleBusinessProfile?.accountId) &&
      !isEmpty(marketingChannel?.googleBusinessProfile?.locationId) &&
      !isEmpty(marketingChannel?.googleBusinessProfile?.refreshToken)
    ) {
      status = 'SUCCESS'
    }

    const disableConnectBtn = !isRCCPlatform
    return getConnectionColumns(
      connectToGoogleBusinessProfile,
      openGoogleBusinessProfile,
      marketingChannel?.id || '',
      status,
      // Always enabled if already connected
      disableConnectBtn && status !== 'SUCCESS',
    )
  }, [marketingChannels, location.id, connectToGoogleBusinessProfile, isRCCPlatform])

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{sectionTitle}</SectionTitle>
      </SectionHeader>
      <TableComponent
        path={'./styles.ts'}
        columns={connectionColumns}
        rowKey='id'
        data={[
          {
            id: uuidv4(),
            type: 'gbp' as TypeName.GBPConnection,
            attributes: {
              title: 'Google Business Profile',
              subtitle: '',
            },
          },
        ]}
      />
      {!isLoading && !isRCCPlatform && (
        <ChilipiperSection
          message='Interested in connecting your POS to Google Business Profile?'
          actionBtnText='Reach out to sales to learn more'
        />
      )}

      {selectedGBPMarketingChannelId && (
        <GoogleBusinessProfileDetailsDrawer
          open
          onClose={() => setSelectedGBPMarketingChannelId('')}
          marketingChannelId={selectedGBPMarketingChannelId}
          partnerId={location.id || ''}
        />
      )}

      {marketingChannelIdParam && location?.id && (
        <ConnectionModal
          isOpen={connectionModalOpen}
          locationName={locationName}
          onClose={() => setConnectionModalOpen(false)}
          showInfoIcon={false}
          marketingChannelId={marketingChannelIdParam}
          locationId={location.id}
        />
      )}
    </Section>
  )
}

export default DigitalMarketingConnections
